import { Dispatch, SetStateAction } from 'react';

import { ApolloError } from 'apollo-client';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  BenefitType,
  IBenefit,
  IBenefitSwap,
  ILoyaltyUserQuery,
  IOffersFragment,
  IRewardFragment,
  IRuleConditionEvaluation,
  useLoyaltyUserQuery,
} from 'generated/graphql-gateway';
import { IAppliedOffer } from 'generated/rbi-graphql';
import {
  INotificationDetailsFragment,
  IRewardOrSystemwideOfferOrSystemwidePromotion,
  ISystemwideOffersFragment,
} from 'generated/sanity-graphql';

import { LoyaltyEngineRewards } from '../types';

export type LoyaltyUser = ILoyaltyUserQuery['loyaltyUser'];

export type AvailableRewards = {
  [id: string]: IRewardFragment;
};

export interface IUseLoyalty {
  loyaltyEnabled: boolean;
  loyaltyLoading: boolean;
  loyaltyUser: LoyaltyUser | null;
  refetchLoyaltyUser: ReturnType<typeof useLoyaltyUserQuery>['refetch'];
  claimPointsEnabled: boolean;
}

export type UseLoyaltyRewards = (loyaltyUser: LoyaltyUser | null) => IUseLoyaltyRewards;

interface IAppliedReward {
  timesApplied: number;
  rewardId: string;
  pointCost: number;
  rewardBenefitId: string;
}

export interface IAppliedRewards {
  [cartId: string]: IAppliedReward;
}
export interface IParsedLoyaltyReward {
  cartId: string;
  rewardId: string;
  timesApplied: number;
}

export interface IParseAppliedRewards {
  appliedRewardsArray: IParsedLoyaltyReward[];
  timesRewardApplied: { [rewardId: string]: number };
}

export interface IUseLoyaltyOffers {
  queryLoyaltyUserOffers: (
    loyaltyId: string,
    cartEntries?: ICartEntry[],
    appliedRewards?: IAppliedRewards,
    subtotalAmount?: number
  ) => void;
  loyaltyOffersEnabled: boolean;
  upsizeAvailable: boolean;
  surpriseAvailable: boolean;
  offers: IOffersFragment[];
  resetUpsizeAvailability: () => void;
  resetSurpriseAvailability: () => void;
  appliedOffers: IAppliedOffer[];
  applyOffer: (offer: IAppliedOffer) => void;
  updateAppliedOffers: (cartEntry: ICartEntry) => void;
  resetAppliedOffers: () => void;
  userOffers: IOffersFragment[];
  queryLoyaltyOffers: (subtotalAmount?: number) => void;
  selectedOffer: ISystemwideOffersFragment | null;
  setSelectedOffer: Dispatch<SetStateAction<ISystemwideOffersFragment | null>>;
  evaluateLoyaltyUserIncentives: (
    loyaltyId: string,
    cartEntries?: ICartEntry[],
    appliedRewards?: IAppliedRewards,
    subtotalAmount?: number
  ) => void;
  offersFeedbackMap: LoyaltyOfferFeedbackMap;
}

export interface IUseLoyaltyRewards {
  applyReward({ cartId, rewardBenefitId }: IRewardAction): void;
  unApplyReward({ cartId, rewardBenefitId }: IRewardAction): void;
  /** An object container applied reward ids and the number of times they have been applied */
  appliedLoyaltyRewards: IAppliedRewards;
  /** An object containing applied reward ids and the number of times they have been applied */
  appliedLoyaltyRewardsArray: IParsedLoyaltyReward[] | null;
  /** Pending loyalty point balance minus applied reward balance */
  stagedCartPoints: number;
  /** Available loyalty rewards to be applied at checkout */
  availableLoyaltyRewardsMap: AvailableRewards;
  /** Create and set available loyalty rewards map to be used at checkout */
  createAvailableLoyaltyRewardsMap(rewards: NonNullable<LoyaltyEngineRewards>): void;
  /** Resets all loyalty reward state */
  resetLoyaltyRewardState(): void;
  totalTimesRewardApplied: IParseAppliedRewards['timesRewardApplied'];
  /** Completely removes staged applied reward based on cartId */
  removeAppliedReward({ cartId, rewardBenefitId }: IRewardAction): void;
  setIsPricingRewardApplication: Dispatch<SetStateAction<boolean>>;
  isPricingRewardApplication: boolean;
  getAvailableRewardFromCartEntry: (cartEntry: ICartEntry) => IRewardFragment | undefined;
}
export interface IUseLoyaltyRewardsFeedback {
  loading: boolean;
  error?: ApolloError;
}

export interface IRewardAction {
  cartId: string;
  rewardBenefitId: string;
  quantity?: number;
}

export interface ICartEntryAdapter {
  sanityId: string;
  lineId: string;
  price: number;
  quantity: number;
  children?: ICartEntryAdapter[];
}

// Swap type guard
export const isSwap = (benefit: IBenefit): benefit is IBenefitSwap => {
  return (benefit as IBenefitSwap)?.value && benefit.type === BenefitType.SWAP;
};

export type NotificationDetails = INotificationDetailsFragment & {
  id: IRewardOrSystemwideOfferOrSystemwidePromotion['loyaltyEngineId'];
};

export interface IUseLoyaltyNotifications {
  notifications: NotificationDetails[];
  loadingNotifications: boolean;
}

export type LoyaltyNotifications = (loyaltyUser: LoyaltyUser | null) => IUseLoyaltyNotifications;

export type RedeemableItem = {
  readonly id: string;
};

export type RedeemableItems = readonly (RedeemableItem | null)[] | null;

export type LoyaltyOfferFeedbackMap = { [offerId: string]: IRuleConditionEvaluation[] };

export enum RuleEvaluationErrorCodes {
  UNSATISFIED_CART_REQUIREMENTS = 'unsatisfied-cart-requirements',
  WITHIN_COOL_DOWN_PERIOD = 'within-cool-down-period',
  EXCEEDS_LIMIT_PER_ORDER = 'exceeds-limit-per-order',
  INVALID_SERVICE_MODE = 'invalid-service-mode',
  INVALID_STORE_ID = 'invalid-store-id',
  NOT_FIRST_ORDER = 'not-first-order',
  NOT_USER_BIRTHDAY = 'not-user-birthday',
  INVALID_END_DATE = 'invalid-end-date',
  INVALID_START_DATE = 'invalid-start-date',
  INVALID_DAY_OF_THE_WEEK = 'invalid-day-of-the-week',
  INVALID_STACKING = 'invalid-stacking',
  EXCEEDS_MAX_REDEMPTION = 'exceeds-max-redemption',
  BELOW_MINIMUM_SPEND = 'below-minimum-spend',
  INVALID_PAYMENT_METHOD = 'invalid-payment-method',
  INSUFFICIENT_POINT_BALANCE = 'insufficient-point-balance',
  INVALID_SWAPS = 'invalid-swaps',
  INVALID_TIME_SPAN = 'invalid-time-span',
  USER_ENROLLMENT = 'user-enrollment',
}
